import { useContext } from "react";
import { barsIcon, searchIcon, targetIcon } from "./icons";
import { FormContextMA } from "../../form-context";
import shouldShowVertical, { hasPrescriptions, hasPersonalGoals, hasHowToReach } from "./minorAilmentVerticalConditions";
import MinorAilmentInfoVertical from "./MinorAilmentInfoVertical";
import GoalsSection from "./GoalsSection";
import { FollowUpTable } from "./ExtraInfo";

export const MinorAilmentInfo = ({ minorAilmentName }) => {
  const { treatment, documentation, education } = useContext(FormContextMA) || {};

  const goals = {
    personalGoals: treatment?.personalGoals ?? "",
    howToAchieveGoals: treatment?.howToAchieveGoals ?? "",
  };

  // whether prescriptions exist for this treatment
  const prescriptionExists = hasPrescriptions(treatment);

  // whether any goals exist
  const personalGoalExists = hasPersonalGoals(goals);
  const howToReachExists = hasHowToReach(goals);
  const goalsExist = personalGoalExists || howToReachExists;

  const showVertical = shouldShowVertical({ documentation, treatment, goals });

  // Only show vertical when the algorithm suggests vertical AND there are
  // NO prescriptions AND NO goals. Otherwise render the horizontal layout.
  const showVerticalFinal = showVertical && !prescriptionExists && !goalsExist;

  const info = {
    description: [
      "An Inflammation of the upper respiratory tract (includes inside of the nose)",
      "This can be caused by allergens such as pollen, mold, dust, and pets",
    ],
    goals: [
      "Provide Relief",
      "Prevent exposure to allergens",
      "Minimize side effects of treatment",
      "Improve quality of life",
    ],
    tips: [
      "Keep windows closed; use air conditioning",
      "Use a dehumidifier",
      "Keep pets out of the bedroom ",
      "Do not keep plants in the house",
      "Avoid tobacco smoke, insect sprays, paint      ",
    ],
  };

  if (showVerticalFinal) {
    return (
      <div className="mb-10">
        <MinorAilmentInfoVertical
          minorAilmentName={minorAilmentName}
          documentation={documentation}
          education={education}
          treatment={treatment}
          goals={goals}
        />
      </div>
    );
  }

  return (
    <div className="mb-10">
      <div className="relative h-full">
        <div className="grid grid-cols-3 gap-[0.3in]">
          <InfoCard
            index={0}
            icon={searchIcon}
            label={`What is ${minorAilmentName}`}
            items={info.description}
          />
          <InfoCard
            index={1}
            icon={barsIcon}
            label="Goals"
            items={info.goals}
          />
          <InfoCard
            index={2}
            icon={targetIcon}
            label="Tips"
            items={info.tips}
          />
        </div>
        <div className="absolute -inset-[0.164285714in]">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 558 272"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
          >
            <path
              d="M1 140.233V13C1 6.37259 6.37258 1 13 1H173.834C180.461 1 185.834 6.37258 185.834 13V259C185.834 265.627 191.206 271 197.834 271H359.667C366.294 271 371.667 265.627 371.667 259V13C371.667 6.37258 377.039 1 383.667 1H545C551.627 1 557 6.37258 557 13V140.233"
              stroke="#017E9D"
              stroke-width="2"
            />
          </svg>
        </div>
      </div>

      {!prescriptionExists && (
        <div className="mt-[0.8in] space-y-[0.8in]">
          <div className="max-w-[100%]">
            <GoalsSection
              personalGoals={goals.personalGoals}
              howToReachGoals={goals.howToAchieveGoals}
            />
          </div>

          <div className="max-w-[100%]">
            <FollowUpTable
              documentation={documentation}
              education={education}
              minorAilmentName={minorAilmentName}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const InfoCard = ({ index = 0, icon = <></>, label = "", items = [] }) => (
  <div className="relative bg-teal-25 rounded-[0.14285714in] p-[0.17142857in]">
    <div className="min-h-[0.9in]">{icon}</div>
    <p className="font-bold text-[0.17142857in] text-teal-300">{label}</p>
    <ul className="mt-[0.17142857in] font-medium  ml-[0.085625in] space-y-[0.11428571in]">
      {items.map((item) => (
        <li
          key={item}
          className="pl-[0.085714286in] text-gray-600 relative text-[0.15714286in]"
        >
          <span className="absolute -left-[0.085714286in] text-REELTeal flex items-center justify-center min-h-[0.17142857in]">
            <svg
              width="0.085714286in"
              height="0.085714286in"
              viewBox="0 0 6 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="2.5" cy="3" r="2" stroke="#017E9D" />
            </svg>
          </span>
          {item}
        </li>
      ))}
    </ul>
    <div
      className={`absolute z-10 ${index === 0 ? "-left-[0.24285714in]" : ""} ${
        index === 1
          ? "-bottom-[0.25in] -translate-x-1/2 left-1/2"
          : "-translate-y-1/2 top-1/2"
      } ${index === 2 ? "-right-[0.25in]" : ""}`}
    >
      <svg
        width="0.2in"
        height="0.2in"
        viewBox="0 0 14 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect x="1" y="1" width="12" height="12" rx="6" fill="white" />
        <rect
          x="1"
          y="1"
          width="12"
          height="12"
          rx="6"
          stroke="#017E9D"
          strokeWidth="2"
        />
        <circle cx="7" cy="7" r="3.5" fill="#017E9D" />
      </svg>
    </div>
  </div>
);
